import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { JourneyTaskProgressRow } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalJourneyDetail } from "./types";

// POST /api/portal/journeys/progress/:progressId/unsnooze — clear snooze
// cols and revert status to 'pending'. Same 3-key invalidation as snooze.

type Input = { progressId: string; assignmentId: string };

type Context = {
  previousDetail: PortalJourneyDetail | undefined;
};

export function useUnsnoozeTask() {
  const queryClient = useQueryClient();
  return useMutation<
    JourneyTaskProgressRow,
    ApiClientError,
    Input,
    Context
  >({
    mutationFn: ({ progressId }) =>
      apiFetch<JourneyTaskProgressRow>(
        `/portal/journeys/progress/${progressId}/unsnooze`,
        { method: "POST" },
      ),
    onMutate: async ({ progressId, assignmentId }) => {
      const key = queryKeys.portal.journeys.detail(assignmentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail =
        queryClient.getQueryData<PortalJourneyDetail>(key);
      queryClient.setQueryData<PortalJourneyDetail>(key, (old) =>
        old
          ? {
              ...old,
              progress: old.progress.map((p) =>
                p.id === progressId
                  ? {
                      ...p,
                      status: "pending",
                      snoozed_until: null,
                      snooze_reason: null,
                    }
                  : p,
              ),
            }
          : old,
      );
      return { previousDetail };
    },
    onError: (err, { assignmentId }, ctx) => {
      if (ctx?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.portal.journeys.detail(assignmentId),
          ctx.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to unsnooze task");
    },
    onSettled: (_data, _err, { assignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.detail(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.openCount(),
      });
    },
  });
}
